import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Play, Download, RefreshCw, Video } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DailyDashboard = () => {
  const { api } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoModal, setVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const fetchTodayRecordings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/recordings/today');
      setRecordings(res.data.recordings);
    } catch (err) {
      toast.error('Failed to fetch recordings');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTodayRecordings();
  }, []);

  const playVideo = (recording) => {
    setSelectedVideo(recording);
    setVideoModal(true);
  };

  const downloadVideo = (recording) => {
    if (!recording.filename) {
      toast.error('Video file not available');
      return;
    }
    const url = `${API_URL}/api/videos/${recording.filename}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${recording.trackingId}-${format(new Date(recording.createdAt), 'yyyy-MM-dd')}.webm`;
    a.click();
    toast.success('Download started');
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const statusBadge = (status) => {
    const styles = {
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      recording: 'bg-red-500/20 text-red-400 border-red-500/30',
      compressed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      uploaded: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      error: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-md border ${styles[status] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
        {status}
      </span>
    );
  };

  // Calculate stats
  const totalDuration = recordings.reduce((sum, r) => sum + (r.duration || 0), 0);
  const avgDuration = recordings.length > 0 ? Math.round(totalDuration / recordings.length) : 0;

  return (
    <div className="p-6 space-y-6" data-testid="daily-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Daily Dashboard</h1>
          <p className="text-zinc-400 mt-1">Today's recording activity - {format(new Date(), 'PPPP')}</p>
        </div>
        <Button onClick={fetchTodayRecordings} variant="outline" className="border-zinc-700" data-testid="refresh-daily-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
                <Video className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{recordings.length}</p>
                <p className="text-sm text-zinc-400">Recordings Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 border border-green-500/30 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{formatDuration(totalDuration)}</p>
                <p className="text-sm text-zinc-400">Total Duration</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{formatDuration(avgDuration)}</p>
                <p className="text-sm text-zinc-400">Avg Duration</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{recordings.filter(r => r.status === 'completed').length}</p>
                <p className="text-sm text-zinc-400">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recordings Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="border-b border-zinc-800 pb-4">
          <CardTitle className="text-lg font-semibold text-white">Today's Recordings</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Tracking ID</TableHead>
                  <TableHead className="text-zinc-400">Executive</TableHead>
                  <TableHead className="text-zinc-400">Time</TableHead>
                  <TableHead className="text-zinc-400">Duration</TableHead>
                  <TableHead className="text-zinc-400">Size</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : recordings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                      No recordings today
                    </TableCell>
                  </TableRow>
                ) : (
                  recordings.map((rec) => (
                    <TableRow key={rec.id} className="border-zinc-800" data-testid={`daily-row-${rec.id}`}>
                      <TableCell className="font-mono text-indigo-400">{rec.trackingId}</TableCell>
                      <TableCell className="text-zinc-300">{rec.executiveName}</TableCell>
                      <TableCell className="text-zinc-400">{format(new Date(rec.createdAt), 'p')}</TableCell>
                      <TableCell className="font-mono text-zinc-400">{formatDuration(rec.duration)}</TableCell>
                      <TableCell className="text-zinc-400">{formatFileSize(rec.fileSize)}</TableCell>
                      <TableCell>{statusBadge(rec.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => playVideo(rec)}
                            disabled={!rec.filename}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadVideo(rec)}
                            disabled={!rec.filename}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Video Player Modal */}
      <Dialog open={videoModal} onOpenChange={setVideoModal}>
        <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              Recording: <span className="text-indigo-400 font-mono">{selectedVideo?.trackingId}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {selectedVideo?.filename && (
              <video
                src={`${API_URL}/api/videos/${selectedVideo.filename}`}
                controls
                className="w-full rounded-lg bg-black"
              />
            )}
            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-zinc-500">Executive</p>
                <p className="text-zinc-300">{selectedVideo?.executiveName}</p>
              </div>
              <div>
                <p className="text-zinc-500">Time</p>
                <p className="text-zinc-300">{selectedVideo && format(new Date(selectedVideo.createdAt), 'p')}</p>
              </div>
              <div>
                <p className="text-zinc-500">Duration</p>
                <p className="text-zinc-300 font-mono">{formatDuration(selectedVideo?.duration)}</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyDashboard;
